import actions from "../config.json";
import { useSessionStorage } from "./useSessionStorage";
import { DEFAULT_BEVERAGES } from "../pages/BeverageV2/constants/default-beverages";
import { useCallback, useState, useEffect } from "react";

export const useBeveragesV2 = () => {
  const storageKey = "storage";
  const currentKey = "currentStorage";
  const descriptionKey = "beverageV2Description";
  const { setItem, getItem } = useSessionStorage("beveragesV2", {
    key: storageKey,
    value: DEFAULT_BEVERAGES,
  });
  const { setItem: setCurrent, getItem: getCurrent } = useSessionStorage("beveragesV2", {
    key: currentKey,
    value: DEFAULT_BEVERAGES,
  })
  const { setItem: setDescription, getItem: getDescription } = useSessionStorage("beveragesV2", {
    key: descriptionKey,
    value: ''
  });
  const [beverages, setBeverages] = useState(getItem(storageKey));
  const [currentBeverages, setCurrentBeverages] = useState(getCurrent(currentKey));

  const clearCurrent = () => {
    setCurrentBeverages(DEFAULT_BEVERAGES);
  };

  const saveBeveragesToStorage = (dataToSave) => {
    const data = dataToSave ?? mergeData(beverages, currentBeverages);
    setItem(storageKey, data);
  }

  const mergeData = (prev, current) => {
    const newData = JSON.parse(JSON.stringify(current));

    Object.keys(current)
      .filter(key => key !== 'plainTextResponse')
      .forEach(key => Object.keys(current[key] ?? {}).forEach(catKey => {
        if (prev[key][catKey] && !newData[key][catKey]) {
          newData[key][catKey] = prev[key][catKey];
        }
      }));
    newData.optedOutPersonalization = current.optedOutPersonalization ?? prev.optedOutPersonalization;
    return newData;
  }

  const hasSomethingSelected = (data) => {
    const hasSomeBeverageAlcoholicLov = Object.keys(data.beverageAlcoholicLov).some(key => data.beverageAlcoholicLov[key]);
    const hasSomeBeverageNonAlcoholicLov = Object.keys(data.beverageNonAlcoholicLov).some(key => data.beverageNonAlcoholicLov[key]);
    return hasSomeBeverageNonAlcoholicLov || hasSomeBeverageAlcoholicLov;
  };

  const splitToTimeAndDrink = (str) => {
    if (!str) {
      return {
        drink: null,
        time: null,
      };
    }
    const [ drink, time ] = str.split(' at ').map(w => w.trim());

    return {
      drink,
      time
    };
  };

  const hasToSpecifyTimeFor = (beverages) => {
    const selectedAlcoholicLovKeys = Object.keys(beverages.beverageAlcoholicLov).filter(key => beverages.beverageAlcoholicLov[key]);
    const selectedNonAlcoholicLovKeys = Object.keys(beverages.beverageNonAlcoholicLov).filter(key => beverages.beverageNonAlcoholicLov[key]);

    return {selectedAlcoholicLovKeys, selectedNonAlcoholicLovKeys};
  };

  const getBeveragesAi = useCallback(async (description, isContinue) => {
    let previousDescription = ``;
    if (isContinue) {
      previousDescription += getDescription(descriptionKey);
    }
    const actualDescription = `
      ${previousDescription}
      ${description}
    `;

    setDescription(descriptionKey, actualDescription);
    try {
      const actionUrl = actions["marriott-passions-v3/get-beverage-preferences-ai"];
      const response = await fetch(`${actionUrl}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ description: actualDescription }),
      });

      const data = await response.json();
      if (!data.haveAQuestion) {
        const newData = mergeData(beverages, data);

        setCurrentBeverages(data);
        setCurrent(currentKey, data);

        setBeverages(newData);
      }


      return {
        hasSelection: hasSomethingSelected(data),
        hasAQuestion: data.haveAQuestion,
        question: data.plainTextResponse
      };
    } catch (err) {
      console.error("Error getting beverage preferences from AI:", err);
      throw err;
    }
  }, [beverages]);

  const setOptConfirm = (optedOutPersonalization) => {
    const data = {
      ...beverages,
      optedOutPersonalization
    };
    setBeverages(data);
    saveBeveragesToStorage(data);
  };

  const handleTime = (currentTime, time) => {
    let timeArr = (currentTime ?? '').split(', ').filter(Boolean);
    if (timeArr.includes(time)) {
      timeArr = timeArr.filter(t => t !== time);
    } else {
      timeArr.push(time);
    }

    if (time === 'anytime') {
      timeArr = timeArr.filter(i => i === time);
    } else {
      timeArr = timeArr.filter(i => i !== 'anytime');
    }

    if (timeArr.length === 3) {
      timeArr = ['anytime'];
    }

    return timeArr;
  };

  const setTime = (key, time) => {

    const alcoholicLovKeys = Object.keys(currentBeverages.beverageAlcoholicDesc);
    const nonAlcoholicLovKeys = Object.keys(currentBeverages.beverageNonAlcoholicDesc);
    if (alcoholicLovKeys.includes(key)) {
      setCurrentBeverages(beverages => {
        const { drink, time: currentTime } = splitToTimeAndDrink(beverages.beverageAlcoholicDesc[key]);
        const timeArr = handleTime(currentTime, time);
        return {
          ...beverages,
          beverageAlcoholicDesc: {
            ...beverages.beverageAlcoholicDesc,
            [key]: `${drink} at ${timeArr.join(', ')}`,
          }
        };
      });
    }

    if (nonAlcoholicLovKeys.includes(key)) {
      setCurrentBeverages(beverages => {
        const { drink, time: currentTime} = splitToTimeAndDrink(beverages.beverageNonAlcoholicDesc[key]);
        const timeArr = handleTime(currentTime, time);
        return {
          ...beverages,
          beverageNonAlcoholicDesc: {
            ...beverages.beverageNonAlcoholicDesc,
            [key]: `${drink} at ${timeArr.join(', ')}`,
          }
        };
      });
    }
  };

  useEffect(() => {
    setCurrent(currentKey, currentBeverages);
  }, [currentBeverages]);

  return {
    getBeveragesAi,
    setBeverages,
    beverages,
    hasToSpecifyTimeFor,
    splitToTimeAndDrink,
    setTime,
    setOptConfirm,
    currentBeverages,
    clearCurrent,
    saveBeveragesToStorage
  };
};